<template>
  <div class="table">
    <table>
      <thead>
        <th>{{ $t('valet.list.plate') }}</th>
        <th>{{ $t('valet.list.firstname') }}</th>
        <th>{{ $t('valet.list.lastname') }}</th>
        <th>{{ $t('valet.list.confirmed') }}</th>
        <th></th>
      </thead>
      <tbody>
        <tr v-for="(row, index) in computedList" :key="index">
          <td>{{ row.plate }}</td>
          <td>{{ row.firstname }}</td>
          <td>{{ row.lastname }}</td>
          <td class="opt-in">
            <IconTimes v-if="!row.confirmed" class="not-confirmed" />
            <IconCircle v-else class="confirmed" />
          </td>
          <td class="actions">
            <Button small use-arrow @click="handleDetailsClick(row.uid)">{{ $t('valet.list.details') }}</Button>
            <Button class="checkin" small use-arrow @click="handleCheckIn(row.uid)">
              <template v-if="row.checkedIn">{{ $t('valet.list.checkout') }}</template>
              <template v-else>{{ $t('valet.list.checkin') }}</template>
            </Button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  import IconTimes from '@/assets/svg/icon-times.svg?inline';
  import IconCircle from '@/assets/svg/icon-circle.svg?inline';
  import Button from '@/components/ui/Button';


  export default {
    name: 'Table',
    components: {
      Button,
      IconTimes,
      IconCircle,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      plateSearch: {
        type: String,
        default: null,
      },
      list: {
        type: Array,
        default: () => [],
      },
      showCheckedins: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        preventCheckInClick: false,
        listData: this.list || [],
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      list(value) {
        this.listData = value;
      },
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedList() {
        const plate = this.plateSearch || null;
        let out = [];

        this.listData.forEach((row) => {
          out.push({
            uid: row.uid,
            plate: row.plate,
            firstname: row.firstname,
            lastname: row.lastname,
            confirmed: row.confirmed,
            checkedIn: this.isCheckedIn(row.checkin),
          });
        });

        if (plate && plate !== '') {
          out = out.filter((row) => {
            const needle = plate.replace(/\s/g, '').toLowerCase();
            const haystack = row.plate.replace(/\s/g, '').toLowerCase();
            return haystack.indexOf(needle) !== -1;
          });
        }

        if (this.showCheckedins) {
          out = out.filter(row => row.checkedIn);
        }
        return out;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Helper Methods
      //----------------------------------
      isCheckedIn(data) {
        const state = data || {};
        // console.log(state, state.checkin !== null && state.checkout === null, state.checkin, state.checkout);
        return state.checkin !== null && state.checkout === null;
      },
      //----------------------------------
      // Event Handler
      //----------------------------------
      handleDetailsClick(uid) {
        this.$router.push(`/${this.$i18n.locale}/valet/details/${uid}`);
      },
      async handleCheckIn(uid) {
        if (!this.preventCheckInClick) {
          this.preventCheckInClick = true;
          const data = this.listData.slice();
          const row = data.find(r => r.uid === uid);
          const time = Math.floor(Date.now() / 1000);
          if (row.checkin && !row.checkin.checkout) {
            await this.$b.api.post('registrations/checkout', null, {
              id: row.checkin.id,
              checkout: time,
            });
            row.checkin = null;
          } else {
            row.checkin = await this.$b.api.post('registrations/checkin', null, {
              rid: row.id,
              checkin: time,
            });
          }
          this.listData = data;
        }
        this.preventCheckInClick = false;
      },
    },
  };
</script>

<style lang="scss">
  @import '~scss/variables';
  @import '~scss/mixins';

  .table {
    width: 100%;
    height: 56vh;
    overflow: scroll;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;

    table {
      width: 100%;
      padding: 0;
      border: 0;
      position: relative;
      border-collapse: collapse;

      thead th {
        background: $color-gainsboro;
        position: sticky;
        top: 0;
        height: 44px;
        z-index: 1;
        @include font-daimler-cs(400);
        font-size: 1.6rem;
        color: $color-black;

        &:first-of-type {
          border-radius: 4px 0 0 0;
        }
        &:last-of-type {
          border-radius: 0 4px 0 0;
        }
      }

      tbody {
        overflow: hidden;
        width: 100%;
        height: 50vh;
        background: $color-whisper;

        tr {
          &:nth-child(even) {
            background: $color-gainsboro;
          }
          td {
            @include font-daimler-cs(300);
            font-size: 1.4rem;
            color: $color-black;
            text-align: center;
            height: 48px;
          }
          td.opt-in {
            position: relative;

            svg {
              position: relative;
              top: 3px;

              &.not-confirmed  {
                width: 18px;
                height: 18px;
                path {
                  fill: $color-error;
                }
              }
              &.confirmed {
                width: 36px;
                height: 36px;
              }
            }
          }
          td.actions {
            width: 32%;

            .button {
              height: 36px;
              margin-right: 16px;
              &:last-of-type {
                margin-right: 8px;
              }

              &.checkin {
                min-width: 110px;
                justify-content: center;
              }
            }
          }
        }
      }
    }
  }
</style>
